// ─────────────────────────────────────────────
// KLOTHIQ — useProducts hook
// ─────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { requireRecycleSuccess } from '@/lib/recycleErrors';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
import type { Database, JerseyIdentifierContext, JerseySkuReview } from '@klothiq/domain';
import { useOperations } from './useOperations';
import { useOperationIntent } from './useOperationIntent';
import { operationsRpc } from '@/lib/operations';

export type Product = Database['public']['Tables']['products']['Row'];
export type ProductVariant = Database['public']['Tables']['product_variants']['Row'];

export type ProductWithVariants = Product & {
  product_variants: ProductVariant[];
  product_types?: { id: string; name: string; code?: string | null } | null;
  target_audiences?: { id: string; name: string; code: string } | null;
  age_groups?: { id: string; name: string; code: string } | null;
};

export const PRODUCTS_KEY = 'products';
export const VARIANTS_KEY = 'variants';

function isMissingColumnError(error: any): boolean {
  if (!error) return false;
  return (
    error.code === 'PGRST204' ||
    error.code === '42703' ||
    error.message?.includes('schema cache') ||
    error.message?.includes('Could not find')
  );
}

function isForeignKeyError(error: any): boolean {
  if (!error) return false;
  return (
    error.code === '23503' ||
    error.message?.includes('foreign key constraint') ||
    error.message?.includes('violates foreign key') ||
    error.details?.includes('Key is not present in table')
  );
}

function sanitizeProductPayload(payload: any, errorMsg?: string): any {
  const cleaned = { ...payload };

  const isValidUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

  // Convert empty strings "" or invalid UUIDs (like 'ADULT') to null for UUID columns
  ['product_type_id', 'target_audience_id', 'age_group_id', 'category_id'].forEach((key) => {
    if (key in cleaned) {
      const val = cleaned[key];
      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed === '' || !isValidUUID(trimmed)) {
          cleaned[key] = null;
          
          // Safe legacy fallback: if it's a code like 'ADULT', store it in the JSONB attributes column!
          if (trimmed !== '') {
            const legacyKey = key.replace('_id', '').toUpperCase(); // e.g., TARGET_AUDIENCE
            cleaned.attributes = cleaned.attributes || {};
            if (!cleaned.attributes[legacyKey]) {
              cleaned.attributes[legacyKey] = trimmed;
            }
          }
        }
      }
    }
  });

  // If database returns missing column or foreign key error, set failing column to null
  if (errorMsg) {
    const msg = errorMsg.toLowerCase();
    if (msg.includes('product_type') || msg.includes('product_types')) {
      cleaned.product_type_id = null;
      delete cleaned.product_type;
    }
    if (msg.includes('target_audience') || msg.includes('target_audiences')) {
      cleaned.target_audience_id = null;
      delete cleaned.target_audience;
    }
    if (msg.includes('age_group') || msg.includes('age_groups')) {
      cleaned.age_group_id = null;
      delete cleaned.age_group;
    }
    if (msg.includes('category') || msg.includes('categories')) {
      cleaned.category_id = null;
      delete cleaned.category;
    }
    if (msg.includes('attributes')) delete cleaned.attributes;
  }

  return cleaned;
}

// ── Query: list all products with their variants ──────────────
export function useProducts(search?: string, filters?: { product_type_id?: string; category_id?: string; showInactive?: boolean }) {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [PRODUCTS_KEY, orgId, search, filters],
    queryFn: async (): Promise<ProductWithVariants[]> => {
      try {
        let query = supabase
          .from('products')
          .select(`
            *,
            product_variants (*)
          `)
          .eq('organization_id', orgId!)
          .order('created_at', { ascending: false });

        if (!filters?.showInactive) {
          query = query.eq('is_active', true);
        }

        const isValidUUID = (str?: string | null) => str ? /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str) : false;

        if (filters?.product_type_id && isValidUUID(filters.product_type_id)) {
          query = query.eq('product_type_id', filters.product_type_id);
        }
        if (filters?.category_id && isValidUUID(filters.category_id)) {
          query = query.eq('category_id', filters.category_id);
        }

        if (search && search.trim()) {
          query = query.or(
            `name.ilike.%${search}%,product_code.ilike.%${search}%,team.ilike.%${search}%`
          );
        }

        const { data, error } = await query;
        let resultData = [];
        
        if (error && isMissingColumnError(error)) {
          // Retry basic query without filters if column missing
          let fallbackQuery = supabase
            .from('products')
            .select(`*, product_variants (*)`)
            .eq('organization_id', orgId!);
          if (!filters?.showInactive) {
            fallbackQuery = (fallbackQuery as any).eq('is_active', true);
          }
          const fallbackResult = await fallbackQuery;
          if (fallbackResult.error) throw fallbackResult.error;
          resultData = (fallbackResult.data as ProductWithVariants[]) ?? [];
          
          if (search && search.trim()) {
            const s = search.toLowerCase();
            resultData = resultData.filter((p: any) => 
              p.name?.toLowerCase().includes(s) || 
              p.product_code?.toLowerCase().includes(s) ||
              p.team?.toLowerCase().includes(s)
            );
          }
        } else if (error) {
          throw error;
        } else {
          resultData = (data as ProductWithVariants[]) ?? [];
        }

        // Apply in-memory fallback filtering for non-UUID strings (e.g., 'APPAREL')
        if (filters?.product_type_id && !isValidUUID(filters.product_type_id)) {
          const typeStr = filters.product_type_id.toLowerCase().trim();
          resultData = resultData.filter((p: any) => 
            p.product_type_id?.toLowerCase().trim() === typeStr || 
            p.attributes?.PRODUCT_TYPE?.toLowerCase().trim() === typeStr || 
            p.attributes?.product_type?.toLowerCase().trim() === typeStr
          );
        }

        return resultData;
      } catch (err: any) {
        if (isMissingColumnError(err)) {
          let fallback = supabase
            .from('products')
            .select(`*, product_variants (*)`)
            .eq('organization_id', orgId!);
          if (!filters?.showInactive) {
            fallback = (fallback as any).eq('is_active', true);
          }
          const fallbackResult = await fallback;
          return (fallbackResult.data as ProductWithVariants[]) ?? [];
        }
        throw err;
      }
    },
    enabled: !!orgId,
    staleTime: 1000 * 30, // 30s
  });
}

// ── Query: single product with variants ───────────────────────
export function useProduct(productId?: string) {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [PRODUCTS_KEY, orgId, productId],
    queryFn: async (): Promise<ProductWithVariants | null> => {
      // Manual refetch bypasses `enabled`, including on the new-product route.
      // Never serialize a missing ID into a PostgREST UUID filter.
      if (!orgId || !productId) return null;
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(productId)) {
        throw new Error('This product link is invalid. Open the product from the Products page.');
      }
      const { data, error } = await supabase
        .from('products')
        .select(`*, product_variants (*)`)
        .eq('id', productId)
        .eq('organization_id', orgId)
        .single();
      if (error) throw error;
      return data as ProductWithVariants;
    },
    enabled: !!orgId && !!productId,
  });
}

function isDuplicateKeyError(error: any): boolean {
  if (!error) return false;
  return (
    error.code === '23505' ||
    error.message?.includes('duplicate key value') ||
    error.message?.includes('unique constraint') ||
    error.details?.includes('already exists')
  );
}

// ── Mutation: create product + variants ───────────────────────
export function useCreateProduct() {
  const queryClient = useQueryClient();
  const { organization, profile } = useAuth();

  return useMutation({
    mutationFn: async (values: {
      product_code: string;
      name: string;
      category_id?: string;
      team?: string;
      sleeve?: string;
      material?: string;
      description?: string;
      product_type_id?: string;
      target_audience_id?: string;
      age_group_id?: string;
      attributes?: Record<string, any>;
    }) => {
      let orgId = organization?.id;
      if (!orgId) {
        const { data: firstOrg } = await supabase.from('organizations').select('id').limit(1).single();
        orgId = firstOrg?.id;
      }
      if (!orgId) throw new Error('Organization not found. Please log in again.');

      // Sync legacy fields with attributes JSONB if needed
      const attrs = values.attributes ?? {};
      const teamVal = values.team ?? attrs.team ?? attrs.TEAM ?? null;
      const sleeveVal = values.sleeve ?? attrs.sleeve ?? attrs.SLEEVE_TYPE ?? null;
      const materialVal = values.material ?? attrs.material ?? attrs.MATERIAL ?? null;

      const payload: any = {
        ...values,
        team: teamVal,
        sleeve: sleeveVal,
        material: materialVal,
        attributes: attrs,
        organization_id: orgId,
        created_by: profile?.id,
        updated_by: profile?.id,
      };

      const safePayload = sanitizeProductPayload(payload);

      try {
        const { data, error } = await supabase
          .from('products')
          .insert(safePayload)
          .select()
          .single();
        if (error) throw error;
        return data;
      } catch (err: any) {
        if (isDuplicateKeyError(err)) {
          // Extract prefix from product_code (e.g. KQ-JER-ESP-AWAY-HS-PEDRI-20-001 -> KQ-JER-ESP-AWAY-HS-PEDRI-20)
          const codeStr = String(safePayload.product_code || '');
          const codeParts = codeStr.split('-');
          const prefix = codeParts.length > 1 ? codeParts.slice(0, -1).join('-') : codeStr;

          const nextSeq = await getNextProductCodeSequence(orgId, prefix);
          const newCode = `${prefix}-${String(nextSeq).padStart(3, '0')}`;
          safePayload.product_code = newCode;

          const retryRes = await supabase.from('products').insert(safePayload).select().single();
          if (retryRes.error) {
            throw new Error(`Product code "${values.product_code}" already exists. Auto-generated retry code "${newCode}" also failed: ${retryRes.error.message}`);
          }
          return retryRes.data;
        }

        if (isMissingColumnError(err) || isForeignKeyError(err)) {
          const errText = `${err.message || ''} ${err.details || ''}`;
          const cleaned = sanitizeProductPayload(safePayload, errText);
          const retryRes = await supabase.from('products').insert(cleaned).select().single();
          if (retryRes.error) throw retryRes.error;
          return retryRes.data;
        }
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['deactivated_products'] });
    },
  });
}

// ── Mutation: update product ──────────────────────────────────
export function useUpdateProduct() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      ...values
    }: Partial<Product> & { id: string }) => {
      const attrs = values.attributes ?? {};
      const teamVal = values.team !== undefined ? values.team : (attrs.team ?? attrs.TEAM ?? undefined);
      const sleeveVal = values.sleeve !== undefined ? values.sleeve : (attrs.sleeve ?? attrs.SLEEVE_TYPE ?? undefined);
      const materialVal = values.material !== undefined ? values.material : (attrs.material ?? attrs.MATERIAL ?? undefined);

      const updateData: any = {
        ...values,
        updated_by: profile?.id,
      };
      if (teamVal !== undefined) updateData.team = teamVal;
      if (sleeveVal !== undefined) updateData.sleeve = sleeveVal;
      if (materialVal !== undefined) updateData.material = materialVal;

      const safeUpdateData = sanitizeProductPayload(updateData);

      try {
        const { data, error } = await supabase
          .from('products')
          .update(safeUpdateData)
          .eq('id', id)
          .select()
          .single();
        if (error) throw error;
        return data;
      } catch (err: any) {
        if (isMissingColumnError(err) || isForeignKeyError(err)) {
          const errText = `${err.message || ''} ${err.details || ''}`;
          const cleaned = sanitizeProductPayload(safeUpdateData, errText);
          const retryRes = await supabase.from('products').update(cleaned).eq('id', id).select().single();
          if (retryRes.error) throw retryRes.error;
          return retryRes.data;
        }
        throw err;
      }
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY, vars.id] });
      queryClient.invalidateQueries({ queryKey: ['deactivated_products'] });
    },
  });
}

// ── Mutation: create variant ──────────────────────────────────
export function useCreateVariant(productId?: string) {
  const queryClient = useQueryClient();
  const { organization, profile } = useAuth();
  const operations = useOperations();
  const jerseyIntent = useOperationIntent(`add-variant:${productId || 'legacy'}`);

  return useMutation({
    mutationFn: async (values: {
      product_id: string;
      sku: string;
      size: string;
      color?: string;
      default_cost?: number;
      default_price?: number;
      initial_stock?: number;
      low_stock_level?: number;
      attributes?: any;
      jersey_review?: JerseySkuReview;
      expected_updated_at?: string;
      jersey_entry?: { details: import('@klothiq/domain').JerseyDetailsV2; sku_mode: 'auto' | 'manual' };
    }) => {
      if (operations.data?.modules.jersey_entry === 2) {
        if (!organization?.id) throw new Error('Organization unavailable. Sign in again.');
        const { data: parent, error } = await supabase.from('products').select('product_type_id,updated_at')
          .eq('id', values.product_id).eq('organization_id', organization.id).single();
        if (error) throw error;
        const context = await operationsRpc<import('@klothiq/domain').JerseyEntryContext>('get_jersey_entry_context_v2', {
          p_profile_id: parent.product_type_id, p_product_id: values.product_id, p_offset: 0,
        });
        if (context.entry_config?.enabled && !context.enabled)
          throw new Error('Optional jersey entry is temporarily unavailable. Keep your values and retry later.');
        if (context.enabled) {
          const requestKey = jerseyIntent.key('create');
          const recovered = await operationsRpc<{ variant_ids?: string[] } | null>('get_operation_result_v1', { p_request_key: requestKey });
          let variantId = recovered?.variant_ids?.[0];
          if (!variantId) {
            const result = await operationsRpc<{ success: boolean; contract_version: number; variant_ids: string[] }>('add_jersey_variants_v2', {
              p_data: { contract_version: 2, request_key: requestKey, product_id: values.product_id,
                expected_updated_at: values.expected_updated_at || parent.updated_at,
                configuration_version_id: context.configuration_version_id, recipe_version: 2,
                details: values.jersey_entry?.details || context.current?.details || {},
                variants: [{ client_key: 'new-variant', size: values.size, colour: values.color || null,
                  sku: { mode: values.jersey_entry?.sku_mode || (values.sku ? 'manual' : 'auto'), manual_value: values.sku },
                  default_cost: values.default_cost ?? 0, default_price: values.default_price ?? 0,
                  low_stock_level: values.low_stock_level ?? 5, opening_stock: values.initial_stock ?? 0,
                  attributes: values.attributes || {},
                }],
              },
            });
            if (!result.success || result.contract_version !== 2 || !result.variant_ids?.[0])
              throw new Error('The variant save could not be confirmed. Recover before retrying.');
            variantId = result.variant_ids[0];
          }
          const { data: variant, error: variantError } = await supabase.from('product_variants').select('*')
            .eq('id', variantId).eq('organization_id', organization.id).single();
          if (variantError) throw variantError;
          jerseyIntent.complete('create');
          return variant;
        }
      }
      if (operations.data?.modules.jersey_identifiers === 1) {
        if (!organization?.id) throw new Error('Organization not available. Sign in again.');
        const { data: parent, error: parentError } = await supabase
          .from('products')
          .select('product_type_id,updated_at')
          .eq('id', values.product_id)
          .eq('organization_id', organization.id)
          .single();
        if (parentError) throw parentError;
        const context = await operationsRpc<JerseyIdentifierContext>(
          'get_jersey_identifier_context_v1',
          {
            p_profile_id: parent.product_type_id,
            p_product_id: values.product_id,
          },
        );
        {
          const key = jerseyIntent.key('create');
          const recovered = await operationsRpc<{ variant_ids?: string[] } | null>(
            'get_operation_result_v1',
            { p_request_key: key },
          );
          let variantId = recovered?.variant_ids?.[0];
          if (!variantId) {
            if (context.enabled && !values.jersey_review?.confirmed)
              throw new Error('Review jersey SKU details before adding this variant.');
            const result = await operationsRpc<{ success: boolean; variant_ids: string[] }>(
              'add_jersey_variants_v1',
              {
                p_data: {
                  contract_version: 1,
                  request_key: key,
                  product_id: values.product_id,
                  expected_updated_at: values.expected_updated_at || parent.updated_at,
                  jersey_review: values.jersey_review,
                  variants: [
                    {
                      sku: context.enabled ? '' : values.sku,
                      size: values.size,
                      color: values.color || '',
                      default_cost: values.default_cost ?? 0,
                      default_price: values.default_price ?? 0,
                      low_stock_level: values.low_stock_level ?? 5,
                      stock_quantity: values.initial_stock ?? 0,
                      attributes: values.attributes || {},
                    },
                  ],
                },
              },
            );
            if (!result.success || !result.variant_ids?.[0])
              throw new Error('The variant save could not be confirmed. Retry to recover.');
            variantId = result.variant_ids[0];
          }
          const { data: variant, error } = await supabase
            .from('product_variants')
            .select('*')
            .eq('id', variantId)
            .eq('organization_id', organization.id)
            .single();
          if (error) throw error;
          jerseyIntent.complete('create');
          return variant;
        }
      }
      let orgId = organization?.id;
      if (!orgId) {
        const { data: firstOrg } = await supabase
          .from('organizations')
          .select('id')
          .limit(1)
          .single();
        orgId = firstOrg?.id;
      }
      if (!orgId) throw new Error('Organization not found. Please log in again.');

      const {
        initial_stock,
        jersey_review: _review,
        expected_updated_at: _version,
        jersey_entry: _entry,
        ...variantData
      } = values;

      try {
        const { data, error } = await supabase
          .from('product_variants')
          .insert({
            ...variantData,
            organization_id: orgId,
            created_by: profile?.id,
            updated_by: profile?.id,
          })
          .select()
          .single();
        if (error) throw error;

        if (data?.id && initial_stock && initial_stock > 0) {
          await supabase.rpc('add_opening_stock', {
            p_data: {
              variant_id: data.id,
              quantity: initial_stock,
              unit_cost: values.default_cost ?? 0,
              reason: 'Initial opening stock on product creation',
            },
          });
        } else if (data?.id) {
          await supabase.from('inventory_items').insert({
            organization_id: orgId,
            variant_id: data.id,
            quantity_on_hand: 0,
            quantity_reserved: 0,
            weighted_avg_cost: values.default_cost ?? 0,
          });
        }

        return data;
      } catch (err: any) {
        if (isDuplicateKeyError(err)) {
          const baseSku = String(variantData.sku || 'SKU');
          let successData = null;

          for (let attempt = 1; attempt <= 10; attempt++) {
            const retrySku = `${baseSku}-${attempt}`;
            const { data: rData, error: rErr } = await supabase
              .from('product_variants')
              .insert({
                ...variantData,
                sku: retrySku,
                organization_id: orgId,
                created_by: profile?.id,
                updated_by: profile?.id,
              })
              .select()
              .single();

            if (!rErr && rData) {
              successData = rData;
              break;
            }
          }

          if (successData?.id) {
            if (initial_stock && initial_stock > 0) {
              await supabase.rpc('add_opening_stock', {
                p_data: {
                  variant_id: successData.id,
                  quantity: initial_stock,
                  unit_cost: values.default_cost ?? 0,
                  reason: 'Initial opening stock on product creation',
                },
              });
            } else {
              await supabase.from('inventory_items').insert({
                organization_id: orgId,
                variant_id: successData.id,
                quantity_on_hand: 0,
                quantity_reserved: 0,
                weighted_avg_cost: values.default_cost ?? 0,
              });
            }
            return successData;
          }
        }
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['jersey_identifiers'] });
      queryClient.invalidateQueries({ queryKey: ['jersey_entry'] });
    },
  });
}

// ── Mutation: update variant ──────────────────────────────────
export function useUpdateVariant() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      ...values
    }: Partial<ProductVariant> & { id: string }) => {
      const { data, error } = await supabase
        .from('product_variants')
        .update({
          ...values,
          updated_by: profile?.id,
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

// ── Mutation: delete variant ──────────────────────────────────
export function useDeleteVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variantId: string) => {
      const { error } = await supabase
        .from('product_variants')
        .update({ is_active: false })
        .eq('id', variantId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

// ── Mutation: soft-delete product ─────────────────────────────
export function useDeactivateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (productId: string) => {
      const { data, error } = await supabase.rpc('deactivate_product', { p_data: { product_id: productId } });
      requireRecycleSuccess(data, error);
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Mutation: soft-delete variant ─────────────────────────────
export function useDeactivateVariant() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (variantId: string) => {
      const { error } = await supabase
        .from('product_variants')
        .update({ is_active: false, updated_by: profile?.id })
        .eq('id', variantId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['deactivated_products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard_kpis'] });
    },
  });
}

// ── Query: list deactivated/deleted products ─────────────────
export function useDeactivatedProducts() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: ['deactivated_products', orgId],
    queryFn: async (): Promise<ProductWithVariants[]> => {
      const { data, error } = await supabase
        .from('products')
        .select(`*, product_variants (*)`)
        .eq('organization_id', orgId!)
        .eq('is_active', false)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return (data as ProductWithVariants[]) ?? [];
    },
    enabled: !!orgId,
  });
}

// ── Mutation: restore soft-deleted product ────────────────────
export function useRestoreProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ product_id }: { product_id: string }) => {
      const { data, error } = await supabase.rpc('restore_product', { p_data: { product_id } });
      requireRecycleSuccess(data, error);
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// Only the guarded atomic server operation may permanently delete products.
export function usePermanentDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ product_id }: { product_id: string }) => {
      const { data, error } = await supabase.rpc('permanently_delete_product', { p_data: { product_id } });
      requireRecycleSuccess(data, error);
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Query: get next sequence number for a product code prefix ──────────
export async function getNextProductCodeSequence(orgId: string | undefined, prefix: string): Promise<number> {
  if (!prefix || prefix === 'KQ') return 1;

  let targetOrgId = orgId;
  if (!targetOrgId) {
    const { data: firstOrg } = await supabase.from('organizations').select('id').limit(1).single();
    targetOrgId = firstOrg?.id;
  }
  if (!targetOrgId) return 1;

  try {
    const { data, error } = await supabase
      .from('products')
      .select('product_code')
      .eq('organization_id', targetOrgId)
      .ilike('product_code', `${prefix}%`);

    if (error || !data || data.length === 0) return 1;

    let maxSeq = 0;
    data.forEach((p: any) => {
      if (p.product_code) {
        const parts = p.product_code.split('-');
        const lastPart = parts[parts.length - 1];
        const num = parseInt(lastPart, 10);
        if (!isNaN(num) && num > maxSeq) {
          maxSeq = num;
        }
      }
    });

    return maxSeq + 1;
  } catch {
    return 1;
  }
}

